import { useEffect, useState } from "react";
import { ShowLessArrow } from "../../../../../assets";
import { Button } from "../../../../../components";
import { BACKEND_AI_AGENT_URL } from "../../../../../config";
import { ComparisonCard } from "./ComparisonCard";

// Example: "en-US"
// undefined - use user's locale
const locale = undefined;

interface Token {
  name: string;
  symbol: string;
  projectId: string;
  icon?: string;
}

const tokens: Token[] = [
  {
    name: "1Inch",
    symbol: "1INCH",
    projectId: "1inch",
  },
  { name: "Chainlink", symbol: "LINK", projectId: "chainlink" },
  { name: "Gala", symbol: "GALA", projectId: "gala" },
  { name: "Axelar", symbol: "AXL", projectId: "axelar" },
  { name: "Toshi", symbol: "TOSHI", projectId: "toshi" },
  { name: "PAAL AI", symbol: "PAAL", projectId: "paal-ai" },
  { name: "Entangle", symbol: "NGL", projectId: "entangle" },
  { name: "Boomerang", symbol: "BOOMER", projectId: "boomerang" },
  { name: "handle.fi", symbol: "FOREX", projectId: "handle-fi" },
  { name: "DackieSwap", symbol: "DACKIE", projectId: "dack" },
];

const mockTokenData = {
  name: "TokenName",
  ticker: "TOKEN",
  price: 0,
  pricePercentage: 0,
  marketCap: 0,
  gatewayScore: 0,
  technicalAnalysisScore: 0,
  fundamentalAnalysisScore: 0,
  socialScore: 0,
};

export const Compare = ({ baseTokenId }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedToken, setSelectedToken] = useState<Token | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [baseTokenData, setBaseTokenData] = useState(mockTokenData);
  const [comparisonTokenData, setComparisonTokenData] = useState(mockTokenData);

  useEffect(() => {
    const fetchTokensData = async () => {
      try {
        const baseTokenResponse = await fetch(
          `${BACKEND_AI_AGENT_URL}/token-comparision-info?projectId=${baseTokenId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        const baseTokenData = await baseTokenResponse.json();

        setBaseTokenData(baseTokenData);

        if (!selectedToken) {
          return;
        }

        const comparisionTokenResponse = await fetch(
          `${BACKEND_AI_AGENT_URL}/token-comparision-info?projectId=${selectedToken?.projectId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        const comparisionTokenData = await comparisionTokenResponse.json();

        setComparisonTokenData(comparisionTokenData);
      } catch (error) {
        console.error("Failed to fetch token data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTokensData();
  }, [baseTokenId, selectedToken]);

  const handleCompare = () => {
    if (selectedToken) {
      setIsLoading(false);
      setShowComparison(true);
    }
  };

  const handleTokenSelect = (token: Token) => {
    setSelectedToken(token);
    setIsVisible(false);
  };

  if (showComparison) {
    return (
      <div className="flex flex-col gap-7 items-stretch w-full relative">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="watermark"></div>
        </div>
        <div className="flex justify-between items-center">
          <h2 className="font-bold">Compare</h2>
          <button
            onClick={() => {
              setShowComparison(false);
              setSelectedToken(null);
            }}
            className="text-[#007DFF]"
          >
            Go Back
          </button>
        </div>

        <div className="flex flex-col gap-4">
          <div className="bg-[#55A6FB1A] p-4 rounded-lg">
            <div className="text-center">
              {comparisonTokenData.name} with the Market Cap of{" "}
              {baseTokenData.name}
              <div className="text-2xl font-bold">
                {(
                  (baseTokenData.marketCap / comparisonTokenData.marketCap) *
                  comparisonTokenData.price
                ).toLocaleString(locale, { maximumFractionDigits: 4 })}{" "}
                USD{" "}
                <span
                  className={`text-sm ${baseTokenData.marketCap / comparisonTokenData.marketCap > 1 ? "text-[#4CAF50]" : "text-[#F44336]"}`}
                >
                  {(
                    baseTokenData.marketCap / comparisonTokenData.marketCap
                  ).toLocaleString(locale, {
                    maximumFractionDigits: 4,
                  })}
                  x
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-rows-2 sm:grid-cols-2 gap-4">
            <ComparisonCard
              isBaseToken={true}
              data={baseTokenData}
              isLoading={isLoading}
            />
            <ComparisonCard
              isBaseToken={false}
              data={comparisonTokenData}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-7 items-stretch w-full">
      <h2 className="font-bold">Compare</h2>

      <div className="w-full flex flex-col sm:flex-row gap-4">
        <div className="w-full flex p-5 items-center rounded-xl bg-[#55A6FB1A]">
          <div className="flex gap-4 items-center">
            <img
              alt=""
              src={`https://mygateway.io/assets/img/tokens/${baseTokenData.ticker}.webp`}
              className="h-[38px] w-[38px] rounded-full bg-[#C4C4C4]"
            />
            <div className="flex gap-1 font-ibm-plex-sans text-[14px] font-medium leading-[20px]">
              <span>{baseTokenData.name}</span>
              <span className="text-[#FFFFFF88]">{baseTokenData.ticker}</span>
            </div>
          </div>
        </div>

        <div className="relative w-full">
          <button
            onClick={() => setIsVisible((prev) => !prev)}
            className="w-full flex p-5 items-center rounded-xl gap-2 border border-[#55A6FB1A] border-solid border-1"
          >
            <div className="flex gap-2 w-full items-center">
              {selectedToken ? (
                <>
                  <img
                    alt=""
                    src={`https://mygateway.io/assets/img/tokens/${selectedToken.symbol}.webp`}
                    className="h-[38px] w-[38px] rounded-full bg-[#C4C4C4]"
                  />
                  <div className="flex gap-1">
                    <span>{selectedToken.name}</span>
                    <span className="text-[#FFFFFF88]">
                      {selectedToken.symbol}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-10 h-10 rounded-full bg-[#C4C4C4]"></div>
                  <p className="opacity-50">Select Token</p>
                </>
              )}
            </div>
            <div>
              <ShowLessArrow className={`${isVisible && "rotate-180"}`} />
            </div>
          </button>

          {isVisible && (
            <div className="absolute max-h-[180px] z-10 overflow-y-auto w-full mt-2 bg-[#09131d] rounded-xl border border-[#55A6FB1A] border-solid border-1 p-2 z-10">
              {tokens
                .filter((t) => t.symbol !== baseTokenData.ticker)
                .map((token) => (
                  <div
                    key={token.symbol}
                    onClick={() => handleTokenSelect(token)}
                    className="flex items-center gap-2 p-3 hover:bg-[#55A6FB1A] rounded-lg cursor-pointer"
                  >
                    <img
                      alt=""
                      src={`https://mygateway.io/assets/img/tokens/${token.symbol}.webp`}
                      className="h-[38px] w-[38px] rounded-full bg-[#C4C4C4]"
                    />
                    <div className="flex gap-1">
                      <span>{token.name}</span>
                      <span className="text-[#FFFFFF88]">{token.symbol}</span>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      <Button
        onClick={handleCompare}
        variant="OUTLINED"
        large
        disabled={!selectedToken}
      >
        {isLoading ? (
          <div className="flex justify-center ">
            <div className="flex justify-center items-center">
              <div className="animate-bounce inline-block h-2 w-2 bg-white rounded-full"></div>
              <div className="animate-bounce inline-block h-2 w-2 bg-white rounded-full ml-1"></div>
              <div className="animate-bounce inline-block h-2 w-2 bg-white rounded-full ml-1"></div>
            </div>
          </div>
        ) : (
          "Compare"
        )}
      </Button>
    </div>
  );
};
